import { canAccess, type SystemRole } from './rail-rbac';
import { RAIL_CONFIG, type RailModule, type RailSubPage, type MobileTabPriority } from './rail-config';
import { isComfyReviewer } from './comfy-review-core';

export interface SessionUser {
  id: string;
  system_role: SystemRole;
  // Clinical role (e.g. 'MD'); needed for reviewer-gated sub-pages
  // (requiresReviewer) since "MD" is not a system_role tier.
  role?: string | null;
}

/**
 * getVisibleRail — apply role gating to RAIL_CONFIG.
 * Hides modules a user cannot access; strips sub-pages a user cannot
 * access; strips quickAdd if the user is below its minRole; hides the
 * module entirely when role gating removed every one of its sub-pages.
 * A module DECLARED without sub-pages (Verificaciones: a flat list of
 * reports) is a plain link and stays — dropping it on `length === 0` hid
 * Verificaciones from the rail and the menu for every role (iPad landscape
 * audit iL4).
 */
export function getVisibleRail(user: SessionUser): RailModule[] {
  return RAIL_CONFIG
    .filter((m) => canAccess(user.system_role, m.minRole))
    .filter((m) => {
      if (m.subPages.length === 0) return true;
      return m.subPages.some((s) => subPageVisible(user, s));
    })
    .map((m) => ({
      ...m,
      subPages: m.subPages.filter((s) => subPageVisible(user, s)),
      quickAdd:
        m.quickAdd && canAccess(user.system_role, m.quickAdd.minRole)
          ? m.quickAdd
          : undefined,
    }));
}

function subPageVisible(user: SessionUser, s: RailSubPage): boolean {
  return canAccess(user.system_role, s.minRole) && (!s.requiresReviewer || isComfyReviewer(user));
}

/**
 * resolveTabPriority — A5: a scalar mobileTabPriority applies to every
 * role; a partial record gives the role-specific number. Returns
 * undefined when the module is not a bottom tab for this role.
 */
function resolveTabPriority(p: MobileTabPriority | undefined, role: SystemRole): number | undefined {
  if (p === undefined) return undefined;
  if (typeof p === 'number') return p;
  return p[role];
}

/**
 * getMobileTabs — the up-to-4 bottom-tab modules for this user, in
 * priority order (highest first). A5: priorities resolve per-role, so
 * the tab set differs by role (e.g. Inbox is a tab for staff/superadmin
 * but not admin). Callers MUST render in the returned order — do not
 * re-filter RAIL_CONFIG, which would revert to declaration order.
 */
export function getMobileTabs(user: SessionUser): RailModule[] {
  return getVisibleRail(user)
    .map((m) => ({ m, pr: resolveTabPriority(m.mobileTabPriority, user.system_role) }))
    .filter((x): x is { m: RailModule; pr: number } => x.pr !== undefined)
    .sort((a, b) => b.pr - a.pr)
    .slice(0, 4)
    .map((x) => x.m);
}

/**
 * getDefaultSubPage — highest-defaultPriority sub-page within a module's
 * (already role-filtered) sub-page list. Used by the bare-module
 * redirect (`/tickets` → `/tickets/activos`) when no last-visited is set.
 */
export function getDefaultSubPage(m: RailModule): RailSubPage | undefined {
  if (m.subPages.length === 0) return undefined;
  return [...m.subPages].sort(
    (a, b) => (b.defaultPriority ?? 0) - (a.defaultPriority ?? 0),
  )[0];
}

const LAST_KEY = (uid: string, mid: string) => `rail:last:${uid}:${mid}`;

/**
 * getLastVisitedSubPage — read the most-recent sub-page id for the
 * (user, module) pair, or null. Safe on SSR (returns null when
 * localStorage is unavailable).
 */
export function getLastVisitedSubPage(userId: string, moduleId: string): string | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(LAST_KEY(userId, moduleId));
  } catch {
    return null;
  }
}

/** setLastVisitedSubPage — persist last-visited sub-page (no-op on SSR). */
export function setLastVisitedSubPage(userId: string, moduleId: string, subPageId: string): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(LAST_KEY(userId, moduleId), subPageId);
  } catch {
    /* swallow quota / private-mode errors */
  }
}
